<script setup lang="ts">
import http from '@/services/http'
import Thermometer from './Thermometer.vue'
import RiskCatalogSelect from '../matrix/RiskCatalogSelect.vue'
import type { RiskOverviewFilter } from '@/models/filters-generated'
import { ref, watch } from 'vue'
import type { Responsible, RiskCatalogMeta, RiskConfig, RiskOverview } from '@/models/api-generated'
import RisksCard from './RisksCard.vue'
import Matrix from './Matrix.vue'
import HighRiskScoreCard from './HighRiskScoreCard.vue'
import CriticalAssets from './CriticalAssets.vue'
import RiskTreatment from './RiskTreatment.vue'
import CriticalMethods from './CriticalMethods.vue'
import MethodsOverview from './MethodsOverview.vue'
import ImportantChanges from './ImportantChanges.vue'
import ApproachingRisks from './ApproachingRisks.vue'
import Threats from './Threats.vue'
import Vulnerabilities from './Vulnerabilities.vue'
import { refreshOverviewCallback } from './overviewShared'
import { generateDueDateParams, genRouteID, viewing } from '@/services/utils'
import ResponsibleDeadlines from './ResponsibleDeadlines.vue'
import { DueDateOverdue } from '@/models/enums-generated'

refreshOverviewCallback.value = fetch
const isLoading = ref(false)
const expanded = ref(false)
const props = defineProps<{ riskConfig: RiskConfig; isDashboard: boolean }>()
const filter = ref<RiskOverviewFilter>({
	catalog: [],
	instanceId: props.riskConfig?.instance?.id ?? 0,
})
const catalogs = ref<RiskCatalogMeta[]>([])
const catalogSelect = ref<InstanceType<typeof RiskCatalogSelect>>()
const model = ref<RiskOverview>()

watch(catalogs, (c) => {
	catalogSelect.value?.search()
	filter.value.catalog = c?.map((x) => x.id) ?? []
	fetch()
	if (!props.isDashboard) handleChildClick()
})

watch(
	() => props.riskConfig.catalogs,
	() => (catalogs.value = props.riskConfig.catalogs),
	{ immediate: true },
)

async function fetch() {
	isLoading.value = true
	const { data } = await http.post('api/risk/overview', filter.value)
	model.value = data.data
	isLoading.value = false
}

const emit = defineEmits<{
	(e: 'go-to-risks', riskNames?: string[], treatments?: string[], catalogs?: RiskCatalogMeta[], responsible?: Responsible): void
}>()

function handleChildClick(name?: string[], treatment?: string[], responsible?: Responsible) {
	if (props.isDashboard) {
		goToRisksPage(name, treatment, responsible)
		return
	}
	emit('go-to-risks', name, treatment, catalogs.value, responsible)
}

function toggleExpand() {
	expanded.value = !expanded.value
}

function goToRisksPage(name?: string[], treatment?: string[], responsible?: Responsible) {
	const params = new URLSearchParams()
	name?.forEach((mod) => {
		params.append('name', mod)
	})
	treatment?.forEach((mod) => {
		params.append('treatment', mod)
	})
	if (responsible) {
		let p = generateDueDateParams(DueDateOverdue)
		for (const [key, value] of p.entries()) {
			params.append(key, value)
		}
		params.append('responsible', JSON.stringify({ id: responsible.id, type: responsible.type }))
	}

	window.location.href = `/app/risk-management?${params}`
}

const fetchView = async (instanceId: number) => {
	await http.get(`/api/view/${genRouteID(instanceId, '', 'Instance')}`)
	window.location.reload()
	window.location.href = `/app/risk-management`
}

defineExpose({
	expanded,
})
</script>

<template>
	<div class="rounded-container mt-3 p-3 mb-3 bg-lightblue">
		<template v-if="model && props.riskConfig.instance">
			<div class="d-flex align-items-center justify-content-between w-100">
				<div class="d-flex align-items-center">
					<button class="btn btn-sm btn-outline-primary me-2" @click="toggleExpand">
						<i :class="expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
					</button>
					<h5 class="me-2 mb-0">{{ props.riskConfig.instance.name }}</h5>
					<div class="ms-2">{{ $t('risk.overview.total') }}: {{ model.risks.total || 0 }}</div>
				</div>

				<Thermometer
					class="mt-2"
					v-model="model.allRiskScores"
					:average="model.averageRiskScore"
					:config="props.riskConfig"
					@go-to-risks="handleChildClick"
				/>
			</div>

			<transition name="fade">
				<div v-if="expanded" class="mt-4">
					<div class="row justify-content-center my-3">
						<div class="col-12 col-md-6 col-lg-4">
							<RiskCatalogSelect
								ref="catalogSelect"
								class="w-100"
								v-model="catalogs"
								:multi-select="true"
								:allow-empty="false"
								:config-id="props.riskConfig.id"
							/>
						</div>
					</div>

					<div class="row mt-3">
						<div class="col">
							<div class="row g-3">
								<div class="col-4">
									<RisksCard v-model="model.risks" class="h-100" @go-to-risks="handleChildClick" />
								</div>
								<div class="col-4">
									<Matrix v-model="model.matrix" class="h-100" @go-to-risks="handleChildClick" :config="props.riskConfig" />
								</div>
								<div class="col-4">
									<HighRiskScoreCard
										v-model="model.highRiskScore"
										class="h-100"
										@go-to-risks="handleChildClick"
										:config="props.riskConfig"
									/>
								</div>

								<div class="col-4">
									<CriticalAssets v-model="model.criticalAssets" class="h-100" :config="props.riskConfig" @go-to-risks="handleChildClick" />
								</div>
								<div class="col-4">
									<RiskTreatment v-model="model.treatment" class="h-100" @go-to-risks="handleChildClick" />
								</div>
								<div class="col-4">
									<ApproachingRisks v-model="model.deadlines" class="h-100" @go-to-risks="handleChildClick" />
								</div>

								<div class="col-4">
									<CriticalMethods
										v-model="model.criticalMethods"
										class="h-100"
										:config="props.riskConfig"
										@go-to-risks="handleChildClick"
									/>
								</div>
								<div class="col-4 methods-overview-card">
									<MethodsOverview v-model="model.methods" class="h-100" />
								</div>
								<div class="col-4">
									<ImportantChanges v-model="model.changes" class="h-100" @go-to-risks="handleChildClick" />
								</div>

								<div class="col-4">
									<Threats v-model="model.threats" class="h-100" @go-to-risks="handleChildClick" :config="props.riskConfig" />
								</div>
								<div class="col-4">
									<Vulnerabilities
										v-model="model.vulnerabilities"
										class="h-100"
										@go-to-risks="handleChildClick"
										:config="props.riskConfig"
									/>
								</div>
								<div class="col-4">
									<ResponsibleDeadlines :filter="filter" class="h-100" @go-to-risks="handleChildClick" />
								</div>
							</div>
						</div>
					</div>
					<div class="d-flex justify-content-center my-3">
						<button v-if="viewing === 0" class="btn btn-sm btn-primary me-2" @click="fetchView(props.riskConfig.instance.id)">
							<i class="bi bi-building"></i>
							{{ $t('risk.overview.openInstance') }}
						</button>
					</div>
				</div>
			</transition>
		</template>

		<div v-else class="p-4 text-center">
			<div class="spinner-border text-primary"></div>
		</div>
	</div>
</template>

<style scoped lang="sass">
.bg-lightblue
  color: var(--bs-body-color)
  background-color: var(--bs-primary-bg-subtle)

.row > .col > .h-100
  height: 100%
</style>
